import React from "react";
import ReactModal from "react-modal";
import CloseIcon from "@mui/icons-material/Close";
import SLMetricsTimer from "../../../pages/email/inbox/SLMetricsTimer";
import "./EmailSLMetricsModal.scss";

interface EmailSLMetricsModalProps {
  isOpen: boolean;
  onClose: () => void;
  emailData: {
    email_first_response_sla_info: any[];
    is_replied: boolean;
    emails: any[];
  };
}

const EmailSLMetricsModal: React.FC<EmailSLMetricsModalProps> = ({
  isOpen,
  onClose,
  emailData,
}) => {
  const customModalStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "90%",
      maxWidth: "700px",
      maxHeight: "85vh",
      padding: "0",
      border: "none",
      borderRadius: "12px",
      overflow: "auto",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1000,
    },
  };

//   const isMobile = window.innerWidth < 600;

// const customModalStyles: any = {
//   content: {
//     top: isMobile ? "0" : "50%",
//     left: isMobile ? "0" : "50%",
//     right: "auto",
//     bottom: "auto",
//     transform: isMobile ? "none" : "translate(-50%, -50%)",
//     width: isMobile ? "100%" : "100%",
//     maxWidth: isMobile ? "100%" : "550px",
//     height: "auto",
//     maxHeight: isMobile ? "600px" : "700px",
//     overflowY: "auto",
//     padding: "20px",
//     borderRadius: isMobile ? "0" : "12px",
//     display: "block",
//   },
// };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getStatusBadge = (sla: any) => {
    if (sla.is_resolved) {
      return <span className="status-badge resolved">Resolved</span>;
    }

    const now = new Date().getTime();
    const scheduledEnd = new Date(sla.sla_scheduled_end_at).getTime();

    if (sla.sla_actual_breach_at || now > scheduledEnd) {
      return <span className="status-badge breached">Breached</span>;
    }

    return <span className="status-badge active">Active</span>;
  };

  const formatDuration = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }
    return `${minutes}m ${seconds}s`;
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customModalStyles}
      contentLabel="Email SL Matrix"
      shouldCloseOnOverlayClick={true}
    >
      <div className="email-sl-metrics-modal">
        <div className="modal-header">
          <h2>Email SL Matrix</h2>
          <button className="close-button" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="modal-body">
          {/* Current Timer Display */}
          {emailData.email_first_response_sla_info?.length > 0 && (
            <div className="current-timer-section">
              <h3>Current First Response SL</h3>
              <div className="timer-display">
                <SLMetricsTimer
                  firstResponseSlaBreach={
                    emailData.email_first_response_sla_info
                  }
                  otherMailDetails={{
                    is_replied: emailData.is_replied,
                    emails: emailData.emails,
                  }}
                />
              </div>
            </div>
          )}

          {/* SLA History */}
          <div className="sla-history-section">
            <h3>SLA history</h3>
            {emailData.email_first_response_sla_info?.length > 0 ? (
              <div className="sla-list">
                {emailData.email_first_response_sla_info.map((sla, index) => (
                  <div key={index} className="sla-item">
                    <div className="sla-header">
                      <div className="sla-level">
                        <span className="label">Escalation level:</span>
                        <span className="value badge-primary">
                          {sla.escalation_level}
                        </span>
                      </div>
                      {getStatusBadge(sla)}
                    </div>

                    <div className="sla-details">
                      <div className="detail-row">
                        <span className="label">Started at:</span>
                        <span className="value">
                          {formatDate(sla.sla_started_at)}
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="label">SL deadline:</span>
                        <span className="value">
                          {formatDate(sla.sla_scheduled_end_at)}
                        </span>
                      </div>

                      {sla.sla_actual_breach_at && (
                        <div className="detail-row highlight-breach">
                          <span className="label">Actual breach:</span>
                          <span className="value">
                            {formatDate(sla.sla_actual_breach_at)}
                          </span>
                        </div>
                      )}

                      <div className="detail-row">
                        <span className="label">SL duration:</span>
                        <span className="value">
                          {formatDuration(sla.sla_duration_ms)}
                        </span>
                      </div>

                      {sla.is_resolved && sla.resolved_at && (
                        <>
                          <div className="detail-row highlight-resolved">
                            <span className="label">Resolved at:</span>
                            <span className="value">
                              {formatDate(sla.resolved_at)}
                            </span>
                          </div>
                          {sla.resolution_method && (
                            <div className="detail-row">
                              <span className="label">Resolution method:</span>
                              <span className="value">
                                {sla.resolution_method}
                              </span>
                            </div>
                          )}
                          {sla.first_response_at && (
                            <div className="detail-row">
                              <span className="label">First response at:</span>
                              <span className="value">
                                {formatDate(sla.first_response_at)}
                              </span>
                            </div>
                          )}
                        </>
                      )}

                      {sla.status_at_breach && (
                        <div className="detail-row">
                          <span className="label">Status at breach:</span>
                          <span className="value" style={{ textTransform: "capitalize"}}>{sla.status_at_breach}</span>
                        </div>
                      )}

                      {sla.sla_emails && sla.sla_emails.length > 0 && (
                        <div className="detail-row">
                          <span className="label">SL emails:</span>
                          <span className="value">
                            {sla.sla_emails.join(", ")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">
                <p>No SLA information available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </ReactModal>
  );
};

export default EmailSLMetricsModal;